import { Component, computed, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink, RouterLinkActive } from '@angular/router';

import { IClientPortalDetailsResponse } from '../models/billing.model';

interface SidebarItem {
  label: string;
  route: string;
  icon: 'bill-pay' | 'reviews' | 'referrals';
}

const BILL_PAY_ITEM: SidebarItem = { label: 'Bill Pay', route: '/bill-pay', icon: 'bill-pay' };
const REVIEW_ITEM: SidebarItem = { label: 'Request a Review', route: '/integrated-reviews', icon: 'reviews' };
const REFERRAL_ITEM: SidebarItem = { label: 'Request a Referral', route: '/referral-request', icon: 'referrals' };

@Component({
  selector: 'app-portal-sidebar',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive],
  template: `
    <aside class="portal-sidebar">
      <ul class="side-nav">
        @for (item of items(); track item.route) {
          <li class="side-nav-item">
            <a
              class="side-nav-link"
              [routerLink]="item.route"
              routerLinkActive="active"
            >
              <span class="menu-icon" aria-hidden="true">
                @if (item.icon === 'bill-pay') {
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="18" height="18" fill="currentColor">
                    <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4zm2-1a1 1 0 0 0-1 1v1h14V4a1 1 0 0 0-1-1H2zm-1 4v5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V7H1z"/>
                    <path d="M2 10a1 1 0 0 1 1-1h2a1 1 0 1 1 0 2H3a1 1 0 0 1-1-1z"/>
                  </svg>
                }
                @if (item.icon === 'reviews') {
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="18" height="18" fill="currentColor">
                    <path d="M3.612 15.443c-.386.198-.824-.149-.746-.592l.83-4.73L.173 6.765c-.329-.314-.158-.888.283-.95l4.898-.696L7.538.792c.197-.39.73-.39.927 0l2.184 4.327 4.898.696c.441.062.612.636.282.95l-3.522 3.356.83 4.73c.078.443-.36.79-.746.592L8 13.187l-4.389 2.256z"/>
                  </svg>
                }
                @if (item.icon === 'referrals') {
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="18" height="18" fill="currentColor">
                    <path d="M15 14s1 0 1-1-1-4-5-4-5 3-5 4 1 1 1 1h8zm-7.978-1A.261.261 0 0 1 7 12.996c.001-.264.167-1.03.76-1.72C8.312 10.629 9.282 10 11 10c1.717 0 2.687.63 3.24 1.276.593.69.758 1.457.76 1.72l-.008.002a.274.274 0 0 1-.014.002H7.022zM11 7a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm3-2a3 3 0 1 1-6 0 3 3 0 0 1 6 0zM6.936 9.28a5.88 5.88 0 0 0-1.23-.247A7.35 7.35 0 0 0 5 9c-4 0-5 3-5 4 0 .667.333 1 1 1h4.216A2.238 2.238 0 0 1 5 13c0-1.01.377-2.042 1.09-2.904.243-.294.526-.569.846-.816zM4.92 10A5.493 5.493 0 0 0 4 13H1c0-.26.164-1.03.76-1.724.545-.636 1.492-1.256 3.16-1.275zM1.5 5.5a3 3 0 1 1 6 0 3 3 0 0 1-6 0zm3-2a2 2 0 1 0 0 4 2 2 0 0 0 0-4z"/>
                  </svg>
                }
              </span>
              <span class="menu-text">{{ item.label }}</span>
            </a>
          </li>
        }
      </ul>
    </aside>
  `,
  styles: [
    `
      :host {
        display: block;
      }
      .portal-sidebar {
        width: 220px;
        min-height: 100%;
        background-color: var(--portal-primary);
        color: rgba(255, 255, 255, 0.9);
        padding: 1.25rem 0;
      }
      .side-nav {
        list-style: none;
        margin: 0;
        padding: 0;
      }
      .side-nav-item + .side-nav-item {
        margin-top: 0.25rem;
      }
      .side-nav-link {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        padding: 0.65rem 1.25rem;
        color: rgba(255, 255, 255, 0.85);
        text-decoration: none;
        font-weight: 500;
        letter-spacing: 0.01em;
        border-left: 3px solid transparent;
        transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
      }
      .side-nav-link:hover {
        background-color: rgba(255, 255, 255, 0.06);
        color: #fff;
      }
      .side-nav-link.active {
        background-color: rgba(var(--portal-chrome-link-rgb), 0.12);
        color: #fff;
        border-left-color: var(--portal-chrome-link);
      }
      .menu-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 20px;
        color: var(--portal-chrome-link);
      }
      .side-nav-link.active .menu-icon {
        color: var(--portal-chrome-link);
      }
      @media (max-width: 575.98px) {
        .portal-sidebar {
          width: 60px;
        }
        .menu-text {
          display: none;
        }
        .side-nav-link {
          justify-content: center;
          padding: 0.65rem 0;
        }
      }
    `,
  ],
})
export class PortalSidebarComponent {
  details = input<IClientPortalDetailsResponse | null>(null);

  items = computed<SidebarItem[]>(() => {
    const details = this.details();
    if (!details) {
      return [];
    }

    const items: SidebarItem[] = [];
    if (details.hasBillPay) {
      items.push(BILL_PAY_ITEM);
    }
    if (details.hasIntegratedReviews) {
      items.push(REVIEW_ITEM);
    }
    if (details.hasReferralRequest) {
      items.push(REFERRAL_ITEM);
    }
    return items;
  });
}
